import React, { HTMLProps, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { IconElements } from '../../../config/icons';
import { NibudCategories, PensionContext } from '../../../contexts/pension-robot/PensionContext';
import { Button } from '../../general/Button';
import { ModalScreenPension } from '../../general/ModalScreen';
import { OverlayHeader } from './OverlayHeader';
import { NibudModalStepHousing } from './NibudModalStepHousing';
import { NibudModalStepCars } from './NibudModalStepCars';
import { NibudModalStepResults } from './NibudModalStepResults';

export const NibudModal: React.FC<{ isShown: boolean; onClose: () => void }> = ({ isShown, onClose }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { expensesInfo },
    } = useContext(PensionContext);
    const step = expensesInfo?.modalStep ?? 1;

    return (
        <ModalScreenPension
            isShown={isShown}
        >
            <div className="bg-white md:rounded-xl p-3 sm:p-8">
                <OverlayHeader
                    title={t('pension-cocktail-fill-screen-nibud-title')}
                    className="mb-8 font-bold"
                    onClose={onClose}
                />
                <NibudModalSectionLabel current={step === 1} text={t('pension-cocktail-fill-screen-nibud-step1')} />
                {step > 1 && (<NibudModalSectionLabel current={step === 2} text={t('pension-cocktail-fill-screen-nibud-step2')} />)}
                {step > 2 && (<NibudModalSectionLabel current={step > 2} text={t('pension-cocktail-fill-screen-nibud-step3')} />)}
                <NibudModalQuestionContentSelector />
                <NibudModelNavButtons onClose={onClose} />
            </div>
        </ModalScreenPension>
    );
};

const NibudModalQuestionContentSelector: React.FC<{}> = () => {
    const {
        parameters: { expensesInfo },
    } = useContext(PensionContext);

    return (
        <div className="flex flex-col my-8">
            {(expensesInfo?.modalStep ?? 1) === 1 && (<NibudModalStepHousing />)}
            {(expensesInfo?.modalStep ?? 1) === 2 && (<NibudModalStepCars />)}
            {(expensesInfo?.modalStep ?? 1) === 4 && (<NibudModalStepResults />)}
        </div>
    )
}

const NibudModalSectionLabel: React.FC<{ current: boolean; text: string }> = ({ current, text }) => {
    return (
        <p
            className={`rounded-full py-4 px-5 font-bold mb-2 ${
                !!current ? 'bg-bnd-green bg-opacity-10 text-bnd-green' : 'bg-bnd-green text-white'
            }`}
        >   
            {!current && (
                <IconElements.Check className="text-bnd-green bg-white inline rounded-full p-1 w-6 h-6 mr-4 mb-1" />
            )}
            {text}
        </p>
    );
};

const NibudModelNavButtons: React.FC<{ onClose: () => void } & HTMLProps<HTMLDivElement>> = ({
    onClose,
    className,
}) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { expensesInfo },
        actions,
    } = useContext(PensionContext);
    const step = expensesInfo?.modalStep ?? 1;

    return (
        <div className={`flex flex-row flex-wrap items-center ${className} `}>
            {step > 1 && (
                <Button
                    id="previous"
                    tint="neutral-no-hover"
                    size="sm"
                    onClick={() => {
                        actions.setParameters({ expensesInfo: { modalStep: step - (step === 4 ? 2 : 1) } });
                    }}
                    className={`font-semibold px-8 w-30 sm:w-44 h-10 mr-2.5`}
                >
                    {t('general-back')}
                </Button>
            )}
            {step === 1 && (
                <Button
                    id="next"
                    tint="p-green"
                    size="sm"
                    onClick={() => {
                        actions.setParameters({ expensesInfo: { modalStep: step + 1 } });
                    }}
                    className={`font-semibold px-8 w-30 sm:w-44 h-10`}
                >
                    {t('general-next')}
                </Button>
            )}
            {step === 2 && (
                <Button
                    id="calculate"
                    tint="p-green"
                    size="sm"
                    onClick={() => {
                        actions.setParameters({ expensesInfo: { modalStep: 4 } });
                    }}
                    className={`font-semibold px-8 w-30 sm:w-44 h-10`}
                >
                    {t('general-calculate')}
                </Button>
            )}
            {step === 4 && (
                <Button
                    id="submit"
                    tint="p-green"
                    size="sm"
                    onClick={() => {
                        actions.setParameters({
                            expensesInfo: {
                                goal: sumGoal(expensesInfo?.nibud?.calculatedExpenses),
                            },
                        });
                        onClose();
                    }}
                    className={`font-semibold px-8 w-30 sm:w-44 h-10`}
                >
                    {t('general-next')}
                </Button>
            )}
        </div>
    );
};

export function sumGoal(expenses?: NibudCategories): number {
    return (
        (expenses?.energyAndLocalTaxes ?? 0) +
        (expenses?.house ?? 0) +
        (expenses?.leisure ?? 0) +
        (expenses?.other ?? 0) +
        (expenses?.otherUtilities ?? 0) +
        (expenses?.transportation ?? 0) +
        (expenses?.household ?? 0) +
        (expenses?.unexpected ?? 0)
    );
}

export function mapNibud(results?: { Uitgavepost: number; Bedrag: number }[]): NibudCategories {
    // In the order of the description on: https://developer.nibud.nl/api-documentation-pensioenuitgaven-v1
    return {
        house: +(results?.find((r) => r.Uitgavepost === 0)?.Bedrag || 0) * 100,
        energyAndLocalTaxes: +(results?.find((r) => r.Uitgavepost === 1)?.Bedrag || 0) * 100,
        transportation: +(results?.find((r) => r.Uitgavepost === 2)?.Bedrag || 0) * 100,
        otherUtilities: +(results?.find((r) => r.Uitgavepost === 3)?.Bedrag || 0) * 100,
        unexpected: +(results?.find((r) => r.Uitgavepost === 4)?.Bedrag || 0) * 100,
        leisure: +(results?.find((r) => r.Uitgavepost === 5)?.Bedrag || 0) * 100,
        household: +(results?.find((r) => r.Uitgavepost === 6)?.Bedrag || 0) * 100,
        other: +(results?.find((r) => r.Uitgavepost === 7)?.Bedrag || 0) * 100,
    };
}




